import { Clock, GitCompare, History, Link2, ListChecks, RefreshCw, Scale, EyeOff } from 'lucide-react'

import { Button, ResourceTile, ResourceTileGrid, StatusMessage, pluralRu } from '@krp/ui-kit'

import { CHECK_STATE_LABELS, CHECK_TRIGGER_LABELS, formatMoment, serverErrorShown } from '../model/labels'
import { CATALOG_SOURCE, type CommonCatalogTile, type ShownError, type SourceTile } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

/** Плитка источника: данные сводки и то, как вид источника называется (метка и слово дают реестр форм подключения приложения). */
export interface SourceTileVm {
  source: SourceTile
  mark: string
  noun: string
  /** Цвет вида источника (`data-source`); `null` — без цвета. */
  tone: string | null
  /** «Проверить сейчас» отправлено и ещё не подтверждено. */
  checkBusy: boolean
  checkError: ShownError | null
}

export interface SyncHomeVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  refreshing: boolean
  tiles: SourceTileVm[]
  catalog: CommonCatalogTile | null
}

export interface SyncHomeActions {
  openDivergences: (source: string) => void
  check: (resourceId: string) => void
  openInit: (resourceId: string) => void
  openIgnores: () => void
  openPriceMaps: () => void
  openSchedule: () => void
  openJournal: () => void
  reload: () => void
}

const count = (value: number, [one, few, many]: readonly [string, string, string]): string => `${value.toLocaleString('ru-RU')} ${pluralRu(value, one, few, many)}`

function checkLine(source: SourceTile): string {
  const check = source.lastCheck
  if (check === null) return 'Проверки ещё не было'
  const when = check.finishedAt ?? check.startedAt
  const progress = check.state === 'running' ? `, прочитано ${check.read}${check.total === null ? '' : ` из ${check.total}`}` : ''
  return `Проверка (${CHECK_TRIGGER_LABELS[check.trigger]}): ${CHECK_STATE_LABELS[check.state]}${progress}, ${formatMoment(when)}`
}

function SourceCard({ tile, actions }: { tile: SourceTileVm; actions: SyncHomeActions }) {
  const { source } = tile
  const running = source.lastCheck?.state === 'running'
  const lines = [
    `Номенклатура: ${source.itemCount.toLocaleString('ru-RU')}`,
    `Штрихкоды: ${source.barcodeCount.toLocaleString('ru-RU')}`,
    source.divergenceCount > 0 ? `Требуют внимания: ${count(source.divergenceCount, ['расхождение', 'расхождения', 'расхождений'])}` : 'Расхождений нет',
    checkLine(source),
  ]
  const lastCheck = source.lastCheck
  return (
    <ResourceTile
      mark={tile.mark}
      noun={tile.noun}
      name={source.displayName}
      tone={tile.tone}
      lines={lines}
      footer={
        <div className="sync-tile__footer">
          <div className="sync__row">
            <Button
              variant={source.divergenceCount > 0 ? 'primary' : 'secondary'}
              icon={<GitCompare size={16} aria-hidden="true" />}
              onClick={() => actions.openDivergences(source.resourceId)}
              actionId="sync.home.open-divergences"
              aria-label={`Расхождения: ${source.displayName}`}
            >
              Расхождения <span className="sync__count">{source.divergenceCount}</span>
            </Button>
            <Button
              variant="secondary"
              icon={<RefreshCw size={16} aria-hidden="true" />}
              busy={tile.checkBusy || running}
              onClick={() => actions.check(source.resourceId)}
              actionId="sync.home.check"
              aria-label={`Проверить сейчас: ${source.displayName}`}
            >
              {running ? 'Проверка идёт…' : 'Проверить сейчас'}
            </Button>
            <Button
              variant="ghost"
              icon={<Link2 size={16} aria-hidden="true" />}
              onClick={() => actions.openInit(source.resourceId)}
              actionId="sync.home.init"
              aria-label={`Инициализация связей: ${source.displayName}`}
            >
              Инициализация связей
            </Button>
          </div>
          {lastCheck !== null && lastCheck.state === 'failed' && lastCheck.errorMessage !== null && (
            <ErrorBlock error={serverErrorShown('Последняя проверка прервалась', lastCheck.errorMessage, lastCheck.errorCode, null)} />
          )}
          {tile.checkError !== null && <ErrorBlock error={tile.checkError} />}
        </div>
      }
    />
  )
}

function CatalogTile({ catalog, actions }: { catalog: CommonCatalogTile; actions: SyncHomeActions }) {
  return (
    <ResourceTile
      mark="ОК"
      noun="Общее хранилище номенклатуры"
      name="Общий каталог"
      tone="common"
      lines={[
        `Номенклатура: ${catalog.productCount.toLocaleString('ru-RU')}`,
        catalog.onlyCommonCount > 0
          ? `Только в каталоге, нет ни в одном источнике: ${catalog.onlyCommonCount.toLocaleString('ru-RU')}`
          : 'Позиций только в каталоге нет',
      ]}
      footer={
        <Button
          variant={catalog.onlyCommonCount > 0 ? 'primary' : 'secondary'}
          icon={<GitCompare size={16} aria-hidden="true" />}
          onClick={() => actions.openDivergences(CATALOG_SOURCE)}
          actionId="sync.home.open-catalog"
        >
          Расхождения <span className="sync__count">{catalog.onlyCommonCount}</span>
        </Button>
      }
    />
  )
}

/**
 * Вход раздела «Синхронизация» (RP-SYN8, требования 4): источники и общий каталог плитками со сводкой. Система только находит
 * расхождения — что с ними делать, решает человек в окне расхождений. Экран ничего не запрашивает сам: vm и действия — от binding.
 */
export function SyncHomeScreen({ vm, actions }: { vm: SyncHomeVm; actions: SyncHomeActions }) {
  return (
    <section aria-labelledby="sync-title" className="sync">
      <header>
        <h2 id="sync-title">Синхронизация</h2>
        <p className="sync__lead">
          Здесь видно, чем источники расходятся между собой и с общим каталогом. Сама система ничего не переносит: вы выбираете
          строки и источники и применяете.
        </p>
      </header>

      <nav className="sync__row" aria-label="Настройки синхронизации">
        <Button variant="ghost" icon={<EyeOff size={16} aria-hidden="true" />} onClick={actions.openIgnores} actionId="sync.home.open-ignores">
          Не отслеживается
        </Button>
        <Button variant="ghost" icon={<Scale size={16} aria-hidden="true" />} onClick={actions.openPriceMaps} actionId="sync.home.open-price-maps">
          Сопоставление цен
        </Button>
        <Button variant="ghost" icon={<Clock size={16} aria-hidden="true" />} onClick={actions.openSchedule} actionId="sync.home.open-schedule">
          Ночная сверка
        </Button>
        <Button variant="ghost" icon={<History size={16} aria-hidden="true" />} onClick={actions.openJournal} actionId="sync.home.open-journal">
          Журнал
        </Button>
      </nav>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем сводку источников…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.home.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && (
        <>
          {vm.refreshing && (
            <p className="sync__note" role="status">
              <ListChecks size={14} aria-hidden="true" /> Сводка обновляется…
            </p>
          )}
          {vm.tiles.length === 0 ? (
            <StatusMessage kind="empty" title="Источников, доступных вам, нет." />
          ) : (
            <ResourceTileGrid label="Источники и общий каталог">
              {vm.catalog !== null && <CatalogTile catalog={vm.catalog} actions={actions} />}
              {vm.tiles.map((tile) => (
                <SourceCard key={tile.source.resourceId} tile={tile} actions={actions} />
              ))}
            </ResourceTileGrid>
          )}
        </>
      )}
    </section>
  )
}
